<template>
  <div style="height:100%">
    <template v-if="loading">
      <downtime-loading />
    </template>
    <template v-else-if="error">
      <downtime-error />
    </template>
    <template v-else-if="!error && !downtime">
      <downtime-no-records />
    </template>
    <template v-else>
      <div
        v-for="(groupData, groupKey, i) in groupedDowntime"
        :key="groupKey"
        :class="i !== 0 ? 'mt-8 mb-2' : 'mb-2'"
        class="headline ml-4 font-weight-medium"
      >
        <div class="primary--text">
          {{ groupKey }}
        </div>
        <v-data-table
          v-model="selected"
          :items="groupData.values"
          :headers="headers"
          hide-default-footer
          item-key="frontendId"
          disable-pagination
          :show-select="toggleSelection"
        >
          <!-- Machine / Line -->
          <template v-slot:[`item.${machineFieldName}`]="{ item }">
            <div class="d-flex align-center">
              <span>
                {{ item[machineFieldName] || item.machinename }}
              </span>
            </div>
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.downtimestart="{ item }">
            {{ formatTime(displayDowntimeStart(item)) }}
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.downtimeend="{ item }">
            <span v-if="!inProgress(item)">
              {{ formatTime(item.downtimeend) }}
            </span>
            <span v-else>-</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.downtimeduration="{ item }">
            <span v-if="!inProgress(item)">
              {{ duration(item) }}
            </span>
            <span v-else>-</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.reasonname="{ item }">
            <assign-downtime :downtime="item" />
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.isPlanned="{ item }">
            <v-icon
              v-if="item.isBreak"
              v-text="'$downtime'"
              color="accent"
              :title="item.breakName"
            ></v-icon>
            <v-icon
              v-else-if="item.isHoliday"
              v-text="'$holidays'"
              color="accent"
              :title="item.holidayName"
            ></v-icon>
            <v-icon
              v-else-if="hasDowntimeReason(item)
                && item.elementName === 'downtime'
                && item.isPlanned === true"
              v-text="'$downtime'"
              color="accent"
            ></v-icon>
            <span v-else>-</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template #item.action="{ item }">
            <downtime-split
              :downtime="item"
              :group-records="item.groupRecords"
              :duration="duration(item)"
              :in-progress="inProgress(item)"
            />
          </template>

          <!-- Remark Column -->
          <template #item.remark="{ item }">
            <div v-if="editRow === item.frontendId">
              <v-text-field v-model="remarkText" dense hide-details placeholder="Enter remark">
              </v-text-field>

              <div class="d-flex mt-2 mb-1">
                <v-btn small color="success"
                class="mr-2" :loading="updatingRemark" :disabled="updatingRemark"
                  @click="updateRemark(item)">
                  Update
                </v-btn>

                <v-btn small color="error" :disabled="updatingRemark" @click="cancelRemark">
                  Cancel
                </v-btn>
              </div>
            </div>

            <div v-else>
              <span class="remark-text" :title="item.remark">
                {{ item.remark || 'Remark' }}
              </span>

              <v-btn icon small @click="startEdit(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </template>
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import DowntimeLoading from './DowntimeLoading.vue';
import DowntimeError from './DowntimeError.vue';
import DowntimeNoRecords from './DowntimeNoRecords.vue';
import DowntimeSplit from './DowntimeSplit.vue';
import AssignDowntime from './AssignDowntime.vue';

export default {
  name: 'DowntimeList',
  components: {
    DowntimeLoading,
    DowntimeError,
    DowntimeNoRecords,
    DowntimeSplit,
    AssignDowntime,
  },
  data() {
    return {
      remarkText: '',
      editRow: null,
      updatingRemark: false,
    };
  },
  computed: {
    ...mapState('downtimeLog', [
      'loading',
      'error',
      'downtimeList',
      'selectedDowntimes',
      'toggleSelection',
    ]),
    ...mapState('webApp', ['appSchema']),
    ...mapGetters('webApp', ['groupedRecords', 'sort', 'sortedRecords']),
    ...mapGetters('downtimeLog', ['downtime', 'machineFilterField']),
    machineFieldName() {
      return this.machineFilterField;
    },
    machineFieldLabel() {
      return this.machineFieldName === 'linename'
        ? 'Line'
        : this.$t('downtime.machine');
    },
    headers() {
      return [
        { text: this.machineFieldLabel, value: this.machineFieldName },
        { text: this.$t('downtime.downtimeStart'), value: 'downtimestart' },
        { text: this.$t('downtime.downtimeEnd'), value: 'downtimeend' },
        { text: this.$t('downtime.duration'), value: 'downtimeduration' },
        {
          text: this.$t('downtime.reason'),
          value: 'reasonname',
          sortable: false,
          width: '30%',
        },
        {
          text: this.$t('downtime.isPlanned'),
          value: 'isPlanned',
          sortable: false,
        },
        {
          text: this.$t('downtime.action'),
          value: 'action',
          sortable: false,
        },
        {
          text: 'Remark',
          value: 'remark',
          sortable: false,
        },
      ];
    },
    downtimetypeIsShift() {
      let downtimetype = false;
      const schemaDetails = Array.isArray(this.appSchema)
        ? this.appSchema.find((schema) => Array.isArray(schema.downtimeDataConfig)
          && schema.downtimeDataConfig.length)
        : null;
      if (
        schemaDetails
        && Array.isArray(schemaDetails.downtimeDataConfig)
        && schemaDetails.downtimeDataConfig.length
      ) {
        const dataConfig = schemaDetails.downtimeDataConfig.find(
          (item) => item.value === 'downtimetypeshift',
        );
        if (dataConfig) {
          downtimetype = dataConfig.display;
        }
      }

      return downtimetype;
    },

    selected: {
      get() {
        return this.selectedDowntimes;
      },
      set(val) {
        this.setSelectedDowntimes(val);
      },
    },

    /**
     * Keeps the original shift/group structure.
     *
     * Inside every shift, records are grouped when:
     * 1. machine/line name is the same
     * 2. reason is the same
     * 3. actualdowntimestart is the same
     * 4. planned/break/holiday context is the same
     * 5. downtime ranges are continuous
     */
    groupedDowntime() {
      if (!this.downtime) {
        return {};
      }

      const filters = this.$store.getters['webApp/filters'];
      const durationFilter = filters && filters.downtimeduration;

      return Object.keys(this.downtime).reduce((result, groupKey) => {
        const groupData = this.downtime[groupKey];

        const records = groupData && Array.isArray(groupData.values)
          ? groupData.values
          : [];

        let processedValues = this.shouldGroupDowntime
          ? this.groupRecords(records)
          : this.createIndividualRecords(records);

        // Apply duration filter AFTER grouping so grouped durations are used
        if (durationFilter && typeof durationFilter.value === 'number' && durationFilter.value > 0) {
          const thresholdSeconds = durationFilter.value;
          processedValues = processedValues.filter((record) => {
            const durationSec = Number(record.downtimeduration) || 0;
            // Skip in-progress records
            if (record.status === 'inProgress') {
              return true;
            }
            if (durationFilter.operation === 'gte') {
              return durationSec >= thresholdSeconds;
            }
            if (durationFilter.operation === 'lte') {
              return durationSec <= thresholdSeconds;
            }
            return true;
          });
        }

        result[groupKey] = {
          ...groupData,
          values: processedValues,
        };

        return result;
      }, {});
    },

    downtimeMode() {
      return this.getSchemaMode(this.appSchema);
      // return 'hourly';
    },

    shouldGroupDowntime() {
      return this.downtimetypeIsShift;
    },

    downtimeSortDirection() {
      const timeSort = this.sort && this.sort.downtimestart;

      if (typeof timeSort === 'string') {
        return timeSort;
      }

      return timeSort && timeSort.value
        ? timeSort.value
        : 'DESC';
    },
    groupedDowntimeCount() {
      return this.countGroupedDowntime(this.groupedDowntime);
    },
    totalGroupedDowntimeCount() {
      if (!this.downtimeList.length) {
        return 0;
      }

      const downtime = this.groupedRecords(this.sortedRecords(this.downtimeList));
      const groupedDowntime = Object.keys(downtime).reduce((result, groupKey) => {
        const groupData = downtime[groupKey];
        const records = groupData && Array.isArray(groupData.values)
          ? groupData.values
          : [];

        result[groupKey] = {
          ...groupData,
          values: this.groupRecords(records),
        };

        return result;
      }, {});

      return this.countGroupedDowntime(groupedDowntime);
    },
  },
  watch: {
    groupedDowntimeCount: {
      immediate: true,
      handler(count) {
        if (this.shouldGroupDowntime) {
          this.setFilteredDowntimeCount(count);
          this.setDowntimeCount(this.totalGroupedDowntimeCount);
        } else {
          this.setFilteredDowntimeCount(count);
          this.setDowntimeCount(this.downtimeList.length);
        }
      },
    },
  },
  methods: {
    ...mapActions('downtimeLog', ['fetchDowntimeList', 'updateReason']),
    ...mapMutations('downtimeLog', [
      'setDowntimeCount',
      'setFilteredDowntimeCount',
      'setSelectedDowntimes',
    ]),
    countGroupedDowntime(downtime) {
      return Object.keys(downtime).reduce((count, groupKey) => (
        count + downtime[groupKey].values.length
      ), 0);
    },

    /**
     * Group continuous downtime records using:
     *
     * actualdowntimestart + machine/line name + reason + planned context
     * + adjacent time ranges
     *
     * Break and holiday context is included in the key so planned rows
     * do not merge with unplanned rows.
     */
    groupRecords(records = []) {
      const groupedRecords = records
        .map((record, index) => ({
          record,
          index,
          start: this.normalizeTimestampToSecond(record.downtimestart),
          end: this.normalizeTimestampToSecond(record.downtimeend),
          actualStart: this.normalizeTimestampToSecond(record.actualdowntimestart),
        }))
        .sort((first, second) => {
          const firstStart = first.start === null
            ? Number.MAX_SAFE_INTEGER
            : first.start;

          const secondStart = second.start === null
            ? Number.MAX_SAFE_INTEGER
            : second.start;

          return firstStart - secondStart;
        })
        .reduce((result, {
          record,
          index,
          start,
          end,
          actualStart,
        }) => {
          const machineName = String(
            record[this.machineFieldName] || record.machinename || '',
          ).trim();
          /*
           * Do not group when:
           * - machine/line name is unavailable
           * - actual downtime start, downtime start, or downtime end is unavailable
           */
          if (
            !machineName
            || actualStart === null
            || start === null
            || end === null
          ) {
            const individualKey = [
              'individual',
              record._id || index,
            ].join('|');

            result.push(
              this.createIndividualRecord(
                record,
                individualKey,
              ),
            );

            return result;
          }

          /*
           * Normalize the display name to avoid differences caused by casing.
           *
           * Example:
           * "Transfer 500" and "transfer 500"
           * will use the same group key.
           */
          const normalizedMachineName = machineName.toLowerCase();

          const shiftName = String(
            record.shiftName || record.shiftname || '',
          ).trim().toLowerCase();

          const reasonKey = this.getReasonKey(record);
          const plannedKey = this.getPlannedKey(record);

          const baseGroupKey = [
            'group',
            shiftName,
            actualStart,
            normalizedMachineName,
            reasonKey,
            plannedKey,
          ].join('|');

          const matchingGroup = result
            .slice()
            .reverse()
            .find((group) => (
              group.baseGroupKey === baseGroupKey
              && this.isContinuousRange(group, start)
            ));

          if (!matchingGroup) {
            const groupKey = [
              baseGroupKey,
              start,
            ].join('|');

            result.push({
              ...record,
              frontendId: groupKey,
              baseGroupKey,
              groupKey,
              groupCount: 1,
              groupRangeEnd: end,

              // Original database records represented by this row.
              groupRecords: [record],

              // Database IDs that must be updated.
              groupRecordIds: record._id
                ? [record._id]
                : [],

            });

            return result;
          }

          matchingGroup.groupRecords.push(record);
          matchingGroup.groupRecords.sort((first, second) => (
            this.normalizeTimestamp(first.downtimestart)
            - this.normalizeTimestamp(second.downtimestart)
          ));

          if (record._id) {
            matchingGroup.groupRecordIds.push(record._id);
          }

          matchingGroup.groupCount = matchingGroup.groupRecords.length;

          const matchingGroupEnd = this.normalizeTimestamp(matchingGroup.downtimeend);

          /*
           * Grouped rows show the full visible range covered by the records.
           */
          if (
            matchingGroupEnd === null
            || end > matchingGroupEnd
          ) {
            matchingGroup.downtimeend = record.downtimeend;
          }

          matchingGroup.groupRangeEnd = Math.max(
            matchingGroup.groupRangeEnd || end,
            end,
          );

          /*
           * Group is considered in progress when any original record
           * is currently in progress.
           */
          if (record.status === 'inProgress') {
            matchingGroup.status = 'inProgress';
          }

          /*
           * Use an existing reason when the grouped row does not have one.
           */
          if (!matchingGroup.reasonname && record.reasonname) {
            matchingGroup.reasonname = record.reasonname;
            matchingGroup.reasonid = record.reasonid;
          }

          /*
           * Use the first available remark.
           */
          if (!matchingGroup.remark && record.remark) {
            matchingGroup.remark = record.remark;
          }

          return result;
        }, []);

      return groupedRecords.map((record) => {
        const start = this.normalizeTimestamp(record.downtimestart);
        const end = this.normalizeTimestamp(record.downtimeend);

        let calculatedDuration = Number(record.downtimeduration) || 0;

        /*
         * For grouped rows, calculate duration using:
         * latest end - earliest start.
         */
        if (
          record.groupCount > 1
          && start !== null
          && end !== null
        ) {
          calculatedDuration = Math.max(
            0,
            Math.floor((end - start) / 1000),
          );
        }

        const { groupRangeEnd, ...groupedRecord } = record;

        return {
          ...groupedRecord,
          downtimeduration: calculatedDuration,
        };
      }).sort((first, second) => {
        const firstStart = this.normalizeTimestamp(first.downtimestart);
        const secondStart = this.normalizeTimestamp(second.downtimestart);

        const firstSortValue = firstStart === null
          ? Number.MAX_SAFE_INTEGER
          : firstStart;

        const secondSortValue = secondStart === null
          ? Number.MAX_SAFE_INTEGER
          : secondStart;

        if (this.downtimeSortDirection === 'DESC') {
          return secondSortValue - firstSortValue;
        }

        return firstSortValue - secondSortValue;
      });
    },

    /**
     * Creates a normalized reason key for grouping.
     */
    getReasonKey(record) {
      const reasonCode = String(record.reasoncode || '').trim();

      if (reasonCode) {
        return `code:${reasonCode.toLowerCase()}`;
      }

      const reasonId = String(record.reasonid || '').trim();

      if (reasonId) {
        return `id:${reasonId}`;
      }

      const reasonName = String(record.reasonname || '').trim();

      if (reasonName) {
        return `name:${reasonName.toLowerCase()}`;
      }

      return 'unassigned';
    },

    isContinuousRange(group, nextStart) {
      const groupEnd = group.groupRangeEnd === undefined
        ? this.normalizeTimestampToSecond(group.downtimeend)
        : group.groupRangeEnd;

      if (groupEnd === null || nextStart === null) {
        return false;
      }

      return Math.abs(groupEnd - nextStart) <= 1000;
    },

    /**
     * Keeps planned/break/holiday rows grouped only with the same planned context.
     */
    getPlannedKey(record) {
      const isBreak = record.isBreak === true;
      const isHoliday = record.isHoliday === true;

      if (isBreak) {
        const breakName = String(record.breakName || '').trim().toLowerCase();
        return `break:${breakName || 'unknown'}`;
      }

      if (isHoliday) {
        const holidayName = String(record.holidayName || '').trim().toLowerCase();
        return `holiday:${holidayName || 'unknown'}`;
      }

      return 'unplanned';
    },

    /**
     * Creates the frontend structure for an individual record.
     */
    createIndividualRecord(record, frontendId) {
      return {
        ...record,
        frontendId,
        groupKey: frontendId,
        groupCount: 1,
        groupRecords: [record],
        groupRecordIds: record._id
          ? [record._id]
          : [],
      };
    },

    createIndividualRecords(records = []) {
      return records.map((record, index) => this.createIndividualRecord(
        record,
        [
          'individual',
          record._id || index,
        ].join('|'),
      ));
    },

    getSchemaMode(schema) {
      if (!schema) {
        return 'hourly';
      }

      if (typeof schema === 'string') {
        try {
          return this.getSchemaMode(JSON.parse(schema));
        } catch (error) {
          return 'hourly';
        }
      }

      if (Array.isArray(schema)) {
        const schemaWithMode = schema.find((item) => (
          item
          && typeof item === 'object'
          && item.mode
        ));

        return schemaWithMode
          ? this.getSchemaMode(schemaWithMode)
          : 'hourly';
      }

      if (
        typeof schema === 'object'
        && typeof schema.mode === 'string'
      ) {
        return schema.mode.trim().toLowerCase();
      }

      return 'hourly';
    },

    /**
     * Converts a timestamp or date string into milliseconds.
     */
    normalizeTimestamp(value) {
      if (
        value === null
        || value === undefined
        || value === ''
      ) {
        return null;
      }

      if (value instanceof Date) {
        return value.getTime();
      }

      const numericValue = Number(value);

      if (!Number.isNaN(numericValue)) {
        return numericValue;
      }

      const parsedTimestamp = new Date(value).getTime();

      return Number.isNaN(parsedTimestamp)
        ? null
        : parsedTimestamp;
    },

    normalizeTimestampToSecond(value) {
      const timestamp = this.normalizeTimestamp(value);

      return timestamp === null
        ? null
        : Math.floor(timestamp / 1000) * 1000;
    },

    /**
     * Formats a timestamp as HH:mm:ss.
     */
    formatTime(value) {
      const timestamp = this.normalizeTimestamp(value);

      if (timestamp === null) {
        return '-';
      }

      return new Date(timestamp).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    },

    displayDowntimeStart(item) {
      return item.downtimestart;
    },
    actualDisplayDowntimeStart(item) {
      return item.actualdowntimestart;
    },
    isBreak(item) {
      return item.isBreak === true;
    },

    /**
     * Formats duration seconds as HH:mm:ss.
     */
    duration(item) {
      const totalSeconds = Number(item.downtimeduration) || 0;

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);

      return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
      ].join(':');
    },

    /**
     * A grouped row is in progress when any original record
     * has status "inProgress".
     */
    inProgress(item) {
      const records = Array.isArray(item.groupRecords)
        ? item.groupRecords
        : [item];

      return records.some(
        (record) => record.status === 'inProgress',
      );
    },

    hasDowntimeReason(item) {
      return !!String(item.reasonname || '').trim();
    },

    startEdit(item) {
      this.editRow = item.frontendId;
      this.remarkText = item.remark || '';
    },

    cancelRemark() {
      this.editRow = null;
      this.remarkText = '';
    },

    /**
     * Updates the remark for every database record represented
     * by the grouped frontend row.
     */
    async updateRemark(item) {
      const records = Array.isArray(item.groupRecords)
        ? item.groupRecords
        : [item];

      const newRemark = this.remarkText.trim();

      const payload = {
        remark: newRemark,
      };

      this.updatingRemark = true;

      try {
        await Promise.all(
          records.map((record) => this.updateReason({
            id: record._id,
            payload,
          })),
        );

        records.forEach((record) => {
          this.$set(record, 'remark', newRemark);
        });

        this.$set(item, 'remark', newRemark);

        this.cancelRemark();
      } catch (remarkUpdateError) {
        console.error(
          'Unable to update grouped downtime remark:',
          remarkUpdateError,
        );
      } finally {
        this.updatingRemark = false;
      }
    },
  },

  created() {
    this.fetchDowntimeList();
  },
};
</script>

<style scoped>
.remark-text {
  max-width: 50px;
  /* adjust width as needed */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
  font-size: small;
}
</style>
